import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import ArticleCard from "@/components/base/ArticleCard";
import RecommendCard from "@/components/base/RecommendCard";
import StarCard from "@/components/base/StarCard";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import CategoryFilters from "@/pages/category/components/CategoryFilters";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getArticlesByCategory, paginate, sortArticles, totalPages } from "@/lib/articles";
import { categoryMeta, type ArticleCategory } from "@/mocks/articles";

const PAGE_SIZE = 6;
const VALID_SLUGS: ArticleCategory[] = ["news", "recommendations", "stars"];

export default function CategoryPage() {
  useScrollToTop();
  const { slug } = useParams<{ slug: string }>();
  const isValid = VALID_SLUGS.includes(slug as ArticleCategory);
  const category = isValid ? categoryMeta[slug as ArticleCategory] : null;

  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [activeType, setActiveType] = useState("all");
  const [activeEra, setActiveEra] = useState("all");
  const [page, setPage] = useState(1);

  useDocumentMeta(
    category ? category.title : "分类未找到 - 蜜桃33",
    category ? category.description : undefined,
  );

  useEffect(() => {
    setSort("latest");
    setActiveType("all");
    setActiveEra("all");
    setPage(1);
  }, [slug]);

  useEffect(() => {
    setPage(1);
  }, [sort, activeType, activeEra]);

  const baseArticles = useMemo(
    () => (isValid ? getArticlesByCategory(slug as ArticleCategory) : []),
    [slug, isValid],
  );

  const allTags = useMemo(() => {
    const set = new Set<string>();
    baseArticles.forEach((item) => item.tags.forEach((tag) => set.add(tag)));
    return Array.from(set);
  }, [baseArticles]);

  const eraTags = useMemo(() => allTags.filter((tag) => /^\d{4}$/.test(tag)), [allTags]);
  const typeTags = useMemo(() => allTags.filter((tag) => !/^\d{4}$/.test(tag)), [allTags]);

  const filtered = useMemo(() => {
    let list = baseArticles;
    if (activeType !== "all") {
      list = list.filter((item) => item.tags.includes(activeType));
    }
    if (activeEra !== "all") {
      list = list.filter((item) => item.tags.includes(activeEra));
    }
    return sortArticles(list, sort);
  }, [baseArticles, activeType, activeEra, sort]);

  const total = filtered.length;
  const pages = totalPages(total, PAGE_SIZE);
  const currentPage = Math.min(page, pages);
  const paged = paginate(filtered, currentPage, PAGE_SIZE);

  const hasFilter = activeType !== "all" || activeEra !== "all";

  const resetFilters = () => {
    setActiveType("all");
    setActiveEra("all");
    setSort("latest");
  };

  if (!isValid || !category) {
    return (
      <div className="mx-auto w-full max-w-[1200px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="没有找到这个分类"
          description="你访问的分类不存在或已下线，可以回到首页继续浏览其他内容。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const isRecommend = category.slug === "recommendations";
  const isStars = category.slug === "stars";

  return (
    <div className="w-full pb-16">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: category.name }]} />
          <div className="mt-4 flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className={`${category.icon} text-2xl`} />
            </span>
            <div className="min-w-0">
              <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
                {category.name}
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
                {category.intro}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-8 w-full max-w-[1200px] px-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <CategoryFilters
              sort={sort}
              onSortChange={setSort}
              typeTags={typeTags}
              activeType={activeType}
              onTypeChange={setActiveType}
              eraTags={eraTags}
              activeEra={activeEra}
              onEraChange={setActiveEra}
              onReset={resetFilters}
              hasFilter={hasFilter}
            />
          </aside>

          <div className="min-w-0">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                共 <strong className="font-semibold text-foreground-900">{total}</strong> 条内容
                {hasFilter && <span className="text-foreground-400"> · 已应用筛选</span>}
              </p>
            </div>

            {paged.length === 0 ? (
              <EmptyState
                title="没有找到符合条件的内容"
                description="试试更换筛选条件，或重置后查看全部内容。"
                actionLabel="重置筛选"
                onAction={resetFilters}
              />
            ) : isRecommend ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4">
                {paged.map((article) => (
                  <RecommendCard key={article.id} article={article} />
                ))}
              </div>
            ) : isStars ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {paged.map((article) => (
                  <StarCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {paged.map((article) => (
                  <ArticleCard key={article.id} article={article} showCategory={false} />
                ))}
              </div>
            )}

            <Pagination page={currentPage} totalPages={pages} onChange={setPage} />
          </div>
        </div>
      </div>
    </div>
  );
}